import { AfterViewInit, Component, Inject, OnInit, signal, computed, DOCUMENT, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { SimplebarAngularModule } from 'simplebar-angular';

import { EventService } from 'src/app/core/services/event.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { ValidatorsService } from 'src/app/services/validators.service';
import { ProfileService } from 'src/app/pages/management/services/profile.service';
import { MENU } from './menu';
import { MenuItem } from './menu.model';

@Component({
  selector: 'app-horizontaltopbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    BsDropdownModule,
    SimplebarAngularModule
  ],
  templateUrl: './horizontaltopbar.component.html',
  styles: [],
})
export class HorizontaltopbarComponent implements OnInit {
  element = signal<any | undefined>(undefined);
  cookieValue = signal('');
  flagvalue: any;
  countryName: any;
  valueset = signal('');
  logoUrl: string | null = null;
  hasLogoError: boolean = false;

  menuItems = signal<MenuItem[]>([]);
  listLang = signal([
    { text: 'Español', flag: 'assets/images/flags/spain.jpg', lang: 'es' },
    { text: 'English', flag: 'assets/images/flags/us.jpg', lang: 'en' },
  ]);

  document = inject(DOCUMENT);
  router = inject(Router);
  eventService = inject(EventService);
  languageService = inject(LanguageService);
  validatorsService = inject(ValidatorsService);
  profileService = inject(ProfileService);

  isSuperAdmin = computed(() => {
    return this.validatorsService.user()?.role?.name === 'SUPER_ADMIN';
  });

  isSuspended = computed(() => {
    return !!this.validatorsService.company()?.customerSubscription?.isSuspended;
  });

  // ─── 1. SUSCRIPCIÓN COMERCIAL ────────────────────────────────
  rawSubExpirationDate = computed(() => {
    return this.validatorsService.company()?.customerSubscription?.expirationDate || null;
  });

  subExpirationDate = computed(() => {
    const raw = this.rawSubExpirationDate();
    if (!raw) return null;
    return new Date(raw);
  });

  subDaysRemaining = computed<number | null>(() => {
    const raw = this.rawSubExpirationDate();
    if (!raw) return null;
    const expStr = typeof raw === 'string' ? raw.substring(0, 10) : new Date(raw).toISOString().substring(0, 10);
    const parts = expStr.split('-').map(Number);
    if (parts.length !== 3) return null;
    
    const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = expDate.getTime() - todayDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  subAlertLevel = computed<'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL' | null>(() => {
    if (this.isSuperAdmin()) return null;
    if (this.isSuspended()) return 'CRITICAL';
    const days = this.subDaysRemaining();
    if (days === null) return null;
    if (days <= 0) return 'CRITICAL';
    if (days <= 5) return 'URGENT';
    if (days <= 15) return 'WARNING';
    if (days <= 30) return 'INFO';
    return null;
  });

  subAlertDismissed = signal(false);

  // ─── 2. TOKEN DELEGADO SIAT ──────────────────────────────────
  rawSiatTokenEndDate = computed(() => {
    return this.validatorsService.company()?.tokenEndDate || null;
  });

  siatTokenEndDate = computed(() => {
    const raw = this.rawSiatTokenEndDate();
    if (!raw) return null;
    return new Date(raw);
  });

  siatTokenDaysRemaining = computed<number | null>(() => {
    const raw = this.rawSiatTokenEndDate();
    if (!raw) return null;
    const expStr = typeof raw === 'string' ? raw.substring(0, 10) : new Date(raw).toISOString().substring(0, 10);
    const parts = expStr.split('-').map(Number);
    if (parts.length !== 3) return null;
    
    const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = expDate.getTime() - todayDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  siatTokenAlertLevel = computed<'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL' | null>(() => {
    if (this.isSuperAdmin()) return null;
    const days = this.siatTokenDaysRemaining();
    if (days === null) return null;
    if (days <= 0) return 'CRITICAL';
    if (days <= 5) return 'URGENT';
    if (days <= 15) return 'WARNING';
    return null;
  });

  siatTokenAlertDismissed = signal(false);

  // ─── 3. FIRMA DIGITAL (SOLO ELECTRONIC_ONLINE) ───────────────
  isElectronicModality = computed(() => {
    return this.validatorsService.company()?.modality === 'ELECTRONIC_ONLINE';
  });

  certEndDate = computed(() => {
    if (!this.isElectronicModality()) return null;
    const endStr = this.profileService.certificate()?.endDate;
    if (!endStr) return null;
    return new Date(endStr);
  });

  certDaysRemaining = computed<number | null>(() => {
    if (!this.isElectronicModality()) return null;
    const endStr = this.profileService.certificate()?.endDate;
    if (!endStr) return null;
    const expStr = typeof endStr === 'string' ? endStr.substring(0, 10) : new Date(endStr).toISOString().substring(0, 10);
    const parts = expStr.split('-').map(Number);
    if (parts.length !== 3) return null;

    const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = expDate.getTime() - todayDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  certAlertLevel = computed<'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL' | null>(() => {
    if (this.isSuperAdmin() || !this.isElectronicModality()) return null;
    const days = this.certDaysRemaining();
    if (days === null) return null;
    if (days <= 0) return 'CRITICAL';
    if (days <= 5) return 'URGENT';
    if (days <= 15) return 'WARNING';
    return null;
  });

  certAlertDismissed = signal(false);

  // ─── 4. ALERTAS ACTIVAS Y PRIORIZADAS (GLOBAL) ───────────────
  activeAlerts = computed(() => {
    if (this.isSuperAdmin()) return [];

    const certLvl = this.certAlertLevel();
    const certDays = this.certDaysRemaining();
    const showCert = certLvl && (!this.certAlertDismissed() || certLvl === 'CRITICAL' || certLvl === 'URGENT');

    const tokenLvl = this.siatTokenAlertLevel();
    const tokenDays = this.siatTokenDaysRemaining();
    const showToken = tokenLvl && (!this.siatTokenAlertDismissed() || tokenLvl === 'CRITICAL' || tokenLvl === 'URGENT');

    const subLvl = this.subAlertLevel();
    const subDays = this.subDaysRemaining();
    const showSub = subLvl && (!this.subAlertDismissed() || subLvl === 'CRITICAL' || subLvl === 'URGENT');

    const activeList: Array<{
      type: 'DIGITAL_SIGNATURE' | 'SIAT_TOKEN' | 'SUBSCRIPTION';
      level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL';
      days: number;
      weight: number;
    }> = [];

    const weights: Record<string, number> = { CRITICAL: 4, URGENT: 3, WARNING: 2, INFO: 1 };

    if (showCert) activeList.push({ type: 'DIGITAL_SIGNATURE', level: certLvl!, days: certDays!, weight: weights[certLvl!] });
    if (showToken) activeList.push({ type: 'SIAT_TOKEN', level: tokenLvl!, days: tokenDays!, weight: weights[tokenLvl!] });
    if (showSub) activeList.push({ type: 'SUBSCRIPTION', level: subLvl!, days: subDays!, weight: weights[subLvl!] });

    if (activeList.length === 0) return [];

    // Ordenar por severidad descendente, luego por días restantes ascendente
    activeList.sort((a, b) => {
      if (b.weight !== a.weight) return b.weight - a.weight;
      return a.days - b.days;
    });

    return activeList.map(item => {
      if (item.type === 'DIGITAL_SIGNATURE') {
        return this.createCertAlertPayload(item.level, item.days);
      } else if (item.type === 'SIAT_TOKEN') {
        return this.createTokenAlertPayload(item.level, item.days);
      } else {
        return this.createSubAlertPayload(item.level, item.days);
      }
    });
  });

  activeAlert = computed(() => {
    const list = this.activeAlerts();
    return list.length > 0 ? list[0] : null;
  });

  hasCriticalAlert = computed(() => {
    return this.activeAlerts().some(alert => alert.level === 'CRITICAL');
  });

  hasNonDismissibleAlert = computed(() => {
    return this.activeAlerts().some(alert => !alert.isDismissible);
  });

  private createCertAlertPayload(level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL', days: number) {
    const compName = this.validatorsService.company()?.companyName || 'mi empresa';
    const nit = this.validatorsService.company()?.nit ? ` (NIT: ${this.validatorsService.company()?.nit})` : '';
    const phone = this.validatorsService.user()?.businessWhatsapp || '59179746003';
    const dateStr = this.certEndDate() ? this.formatDate(this.certEndDate()!) : '';

    let text = '';
    if (level === 'CRITICAL') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Certificado de Firma Digital vencido y requiero asistencia para renovarlo e instalarlo ante el SIAT.`;
    } else if (level === 'URGENT') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Firma Digital por vencer en ${days} día(s) (el ${dateStr}) y requiero asistencia para la renovación de firma electrónica.`;
    } else {
      text = `Hola, mi empresa ${compName}${nit} tiene su Firma Digital por vencer en ${days} días y deseo coordinar su renovación ante ADSIB/proveedor.`;
    }

    return {
      type: 'DIGITAL_SIGNATURE' as const,
      level,
      daysRemaining: days,
      expirationDate: this.certEndDate(),
      whatsappUrl: `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      isDismissible: level === 'WARNING' || level === 'INFO'
    };
  }

  private createTokenAlertPayload(level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL', days: number) {
    const compName = this.validatorsService.company()?.companyName || 'mi empresa';
    const nit = this.validatorsService.company()?.nit ? ` (NIT: ${this.validatorsService.company()?.nit})` : '';
    const phone = this.validatorsService.user()?.businessWhatsapp || '59179746003';
    const dateStr = this.siatTokenEndDate() ? this.formatDate(this.siatTokenEndDate()!) : '';

    let text = '';
    if (level === 'CRITICAL') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Token SIAT vencido y requiero soporte para actualizarlo ante el SIN.`;
    } else if (level === 'URGENT') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Token SIAT por vencer en ${days} día(s) (el ${dateStr}) y requiero asistencia para delegar el nuevo token ante el SIN.`;
    } else {
      text = `Hola, mi empresa ${compName}${nit} tiene su Token SIAT por vencer en ${days} días y requiero asistencia para renovarlo ante el SIN.`;
    }

    return {
      type: 'SIAT_TOKEN' as const,
      level,
      daysRemaining: days,
      expirationDate: this.siatTokenEndDate(),
      whatsappUrl: `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      isDismissible: level === 'WARNING' || level === 'INFO'
    };
  }

  private createSubAlertPayload(level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL', days: number) {
    const compName = this.validatorsService.company()?.companyName || 'mi empresa';
    const nit = this.validatorsService.company()?.nit ? ` (NIT: ${this.validatorsService.company()?.nit})` : '';
    const phone = this.validatorsService.user()?.businessWhatsapp || '59179746003';
    const dateStr = this.subExpirationDate() ? this.formatDate(this.subExpirationDate()!) : '';

    let text = '';
    if (level === 'CRITICAL') {
      text = `Hola, mi cuenta ${compName}${nit} se encuentra ${this.isSuspended() ? 'suspendida' : 'vencida'} por falta de pago y deseo regularizar mi suscripción.`;
    } else if (level === 'URGENT') {
      text = `Hola, mi cuenta ${compName}${nit} vence en ${days} día(s) (el ${dateStr}) y deseo renovar mi suscripción con urgencia.`;
    } else {
      text = `Hola, mi cuenta ${compName}${nit} vence en ${days} días y deseo gestionar la renovación de mi suscripción.`;
    }

    return {
      type: 'SUBSCRIPTION' as const,
      level,
      daysRemaining: days,
      expirationDate: this.subExpirationDate(),
      whatsappUrl: `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      isDismissible: level === 'WARNING' || level === 'INFO'
    };
  }

  private formatDate(d: Date): string {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${d.getFullYear()}`;
  }

  openRefreshTokenModal(): void {
    this.profileService.showModalToken = true;
  }

  closeAlert(type: 'DIGITAL_SIGNATURE' | 'SIAT_TOKEN' | 'SUBSCRIPTION'): void {
    if (type === 'DIGITAL_SIGNATURE') {
      this.certAlertDismissed.set(true);
    } else if (type === 'SIAT_TOKEN') {
      this.siatTokenAlertDismissed.set(true);
    } else if (type === 'SUBSCRIPTION') {
      this.subAlertDismissed.set(true);
    }
  }

  closeActiveAlert(): void {
    const current = this.activeAlert();
    if (current) this.closeAlert(current.type);
  }

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.activateMenu();
      }
    });
  }

  ngOnInit(): void {
    this.profileService.loadCertificate(this.validatorsService.company());
    const logoStorage = localStorage.getItem('logo');
    if (logoStorage && logoStorage.trim() !== '' && logoStorage !== 'null' && logoStorage !== 'undefined') {
      this.logoUrl = logoStorage;
    } else {
      this.logoUrl = null;
    }

    this.element.set(document.documentElement);
    this.menuItems.set(MENU);

    this.cookieValue.set(localStorage.getItem('lang') ?? '');
    const val = this.listLang().filter(x => x.lang === this.cookieValue());
    this.countryName = val.map(element => element.text);
    if (val.length === 0) {
      if (this.flagvalue === undefined) { this.valueset.set('assets/images/flags/us.jpg') }
    } else {
      this.flagvalue = val.map(element => element.flag);
    }
  }

  get getUserName(): string {
    const user = this.validatorsService.user();
    if (!user) return 'Usuario';
    return `${user.fullName || ''} ${user.lastName || ''}`.trim() || 'Usuario';
  }

  get userInitials(): string {
    const user = this.validatorsService.user();
    if (!user) return 'U';
    const first = user.fullName ? user.fullName.trim().charAt(0).toUpperCase() : '';
    const last = user.lastName ? user.lastName.trim().charAt(0).toUpperCase() : '';
    const initials = `${first}${last}`.trim();
    return initials.length > 0 ? initials : (first || 'U');
  }

  get showLogoImage(): boolean {
    return !!this.logoUrl && this.logoUrl.trim() !== '' && this.logoUrl !== 'null' && this.logoUrl !== 'undefined' && !this.hasLogoError;
  }

  onLogoError(): void {
    this.hasLogoError = true;
  }

  _removeAllClass(className: string) {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }

  private activateMenu() {

    const resetParent = (el: any) => {
      const parent = el.parentElement;
      if (parent) {
        parent.classList.remove('active');
        const parent2 = parent.parentElement;
        this._removeAllClass('mm-active');
        this._removeAllClass('mm-show');
        if (parent2) {
          parent2.classList.remove('active');
          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.remove('active');
            const parent4 = parent3.parentElement;
            if (parent4) {
              parent4.classList.remove('active');
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.remove('active');
                const menuelement = document.getElementById("topnav-menu-content")
                if (menuelement !== null) {
                  if (menuelement.classList.contains('show'))
                    document.getElementById("topnav-menu-content")?.classList.remove("show");
                }
              }
            }
          }
        }
      }
    };

    // activate menu item based on location
    const links: any = document.getElementsByClassName('side-nav-link-ref');
    let matchingMenuItem = null;
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < links.length; i++) {
      // reset menu
      resetParent(links[i]);
    }
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < links.length; i++) {
      // tslint:disable-next-line: no-string-literal
      if (location.pathname === links[i]['pathname']) {
        matchingMenuItem = links[i];
        break;
      }
    }

    if (matchingMenuItem) {
      const parent = matchingMenuItem.parentElement;
      /**
       * TODO: This is hard coded way of expading/activating parent menu dropdown and working till level 3.
       * We should come up with non hard coded approach
       */
      if (parent) {
        parent.classList.add('active');
        const parent2 = parent.parentElement;
        if (parent2) {
          parent2.classList.add('active');
          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.add('active');
            const parent4 = parent3.parentElement;
            if (parent4) {
              parent4.classList.add('active');
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.add('active');
                const parent6 = parent5.parentElement;
                if (parent6) {
                  parent6.classList.add('active');
                }
              }
            }
          }
        }
      }
    }
  }


  toggleMenubar() {
    const element = document.getElementById('topnav-menu-content');
    element?.classList.toggle('show');
  }

  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.cookieValue.set(lang);
    this.languageService.setLanguage(lang);
  }

  fullscreen() {
    document.body.classList.toggle('fullscreen-enable');
    if (
      !document.fullscreenElement && !(document as any).mozFullScreenElement &&
      !(document as any).webkitFullscreenElement) {
      if (this.element().requestFullscreen) {
        this.element().requestFullscreen();
      } else if (this.element().mozRequestFullScreen) {
        /* Firefox */
        this.element().mozRequestFullScreen();
      } else if (this.element().webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        this.element().webkitRequestFullscreen();
      } else if (this.element().msRequestFullscreen) {
        /* IE/Edge */
        this.element().msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if ((this.document as any).mozCancelFullScreen) {
        /* Firefox */
        (this.document as any).mozCancelFullScreen();
      } else if ((this.document as any).webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        (this.document as any).webkitExitFullscreen();
      } else if ((this.document as any).msExitFullscreen) {
        /* IE/Edge */
        (this.document as any).msExitFullscreen();
      }
    }
  }

  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }

  onMenuClick(event: any) {
    const nextEl = event.target.nextElementSibling;
    if (nextEl) {
      const parentEl = event.target.parentNode;
      if (parentEl) {
        parentEl.classList.remove("show");
      }
      nextEl.classList.toggle("show");
    }
    return false;
  }

  logout() {
    this.router.navigate(['/auth/']);
  }

  onSettingsButtonClicked() {
    document.body.classList.toggle('right-bar-enabled');
  }
}
